import React, { useState, useEffect } from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
interface FormData {
  email: string;
  type: 'user' | 'merchant' | 'enterprise';
  ageConfirm: boolean;
}

const VisionPage: React.FC = () => {
  const [modalOpen, setModalOpen] = useState<boolean>(false);
  
    const [formData, setFormData] = useState<FormData>({
      email: '',
      type: 'user',
      ageConfirm: false
    });
  const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitMessage, setSubmitMessage] = useState('');

  useEffect(() => {
    document.title = 'Vision — The Future of Loyalty Rewards | Rewardly Protocol';

    const setMeta = (name: string, content: string) => {
      let el = document.querySelector(`meta[name="${name}"]`) || document.querySelector(`meta[property="${name}"]`);
      if (!el) {
        el = document.createElement('meta');
        if (name.startsWith('og:') || name.startsWith('article:')) {
          el.setAttribute('property', name);
        } else {
          el.setAttribute('name', name);
        }
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    };

    setMeta('description', '$300B in loyalty value issued annually. Nearly half wasted. Rewardly Protocol inverts the model — value guaranteed at earn time, not redemption.');
    setMeta('og:title', 'The Future of Loyalty — Rewardly Protocol Vision');
    setMeta('og:description', 'Trust as currency. Value guaranteed at earning. The vision for universal earn-time loyalty rewards infrastructure.');
    setMeta('og:type', 'website');
    setMeta('og:url', 'https://rewardly.live/vision');
    setMeta('og:image', 'https://rewardly.live/og-image.png');
    setMeta('og:site_name', 'Rewardly Protocol');
    setMeta('twitter:card', 'summary_large_image');
    setMeta('twitter:site', '@Rewardly_io');
    setMeta('twitter:title', 'The Future of Loyalty — Rewardly Protocol Vision');
    setMeta('twitter:description', 'Trust as currency. Value guaranteed at earning. The vision for universal earn-time loyalty rewards infrastructure.');
    setMeta('twitter:image', 'https://rewardly.live/og-image.png');

    let canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement;
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', 'https://rewardly.live/vision');
  }, []);

  useEffect(() => {
    if (modalOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [modalOpen]);
const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };
    const handleSubmit = async (e: React.FormEvent): Promise<void> => {
      e.preventDefault();
      
      // Validate email
      if (!validateEmail(formData.email)) {
        setSubmitMessage('Please enter a valid email address');
        return;
      }
  
      // Validate age confirmation
      if (!formData.ageConfirm) {
        setSubmitMessage('Please confirm you are 18 or older');
        return;
      }
      setIsSubmitting(true);
      setSubmitMessage('');
      try {
        const response = await fetch('https://5g72iuzmj9.execute-api.us-east-1.amazonaws.com/dev/save', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ 
            email: formData.email,
            user_type: formData.type
          })
        });
  
        const raw = await response.json();
        let data: any;
        if (raw.body && typeof raw.body === 'string') {
          try { data = JSON.parse(raw.body); } catch { data = raw; }
        } else { data = raw; }
        const statusCode = raw.statusCode || response.status;
        if (statusCode === 200 && !data.error) {
          if (data.new_subscription) {
            setSubmitMessage('Welcome! You\'re on the list. Check your email for confirmation. Follow @Rewardly_io for updates.');
          } else {
            setSubmitMessage('You\'re already on the waitlist! We\'ll email you when early access opens.');
          }
          setFormData({ email: '', type: 'user', ageConfirm: false });
          setTimeout(() => {
            setModalOpen(false);
            setSubmitMessage('');
          }, 3000);
        } else {
          setSubmitMessage(data.error || 'Failed to join waitlist. Please try again.');
        }
      } catch (error) {
        console.error('Error submitting email:', error);
        setSubmitMessage('An error occurred. Please try again.');
      } finally {
        setIsSubmitting(false);
      }
    };
  
    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>): void => {
      const target = e.target as HTMLInputElement;
      const { name, value, type, checked } = target;
      setFormData(prev => ({
        ...prev,
        [name]: type === 'checkbox' ? checked : value
      }));
      // Clear error message when user starts typing
      if (submitMessage) setSubmitMessage('');
    };

  return (
    <div className="min-h-screen bg-white text-gray-900">
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&family=Crimson+Text:ital,wght@0,400;0,600;1,400&family=Outfit:wght@400;500;600;700;800&display=swap');
        
        :root {
          --white: #FFFFFF;
          --off-white: #F8FAFC;
          --gray-100: #F1F5F9;
          --gray-200: #E2E8F0;
          --gray-400: #94A3B8;
          --gray-500: #64748B;
          --gray-900: #0F172A;
          --blue-600: #2563EB;
          --blue-700: #1D4ED8;
          --gold: #D4A853;
          --gold-dark: #B8923A;
          --green-600: #059669;
        }

        * { margin: 0; padding: 0; box-sizing: border-box; }
        html { scroll-behavior: smooth; }
        
        body {
          font-family: 'Inter', -apple-system, sans-serif;
          background: var(--white);
          color: var(--gray-900);
          line-height: 1.6;
          -webkit-font-smoothing: antialiased;
        }

        h1, h2, h3, .section-title, .stat-value, .inversion-title {
          font-family: 'Outfit', 'Inter', -apple-system, sans-serif;
        }

        .container {
          max-width: 1140px;
          margin: 0 auto;
          padding: 0 24px;
        }

        nav {
          position: fixed;
          top: 0;
          width: 100%;
          background: rgba(255,255,255,0.95);
          backdrop-filter: blur(12px);
          border-bottom: 1px solid var(--gray-200);
          z-index: 1000;
        }

        .nav-inner {
          display: flex;
          justify-content: space-between;
          align-items: center;
          height: 72px;
        }

        .nav-menu {
          display: flex;
          align-items: center;
          gap: 32px;
        }

        .nav-menu > a, .dropdown > a {
          color: var(--gray-900);
          text-decoration: none;
          font-weight: 500;
          font-size: 15px;
          transition: color 0.2s;
          cursor: pointer;
        }

        .nav-menu > a:hover, .dropdown > a:hover { color: var(--blue-600); }

        /* Dropdown Menu */
        .dropdown {
          position: relative;
        }

        .dropdown-toggle {
          display: flex;
          align-items: center;
          gap: 4px;
        }

        .dropdown-toggle::after {
          content: '▼';
          font-size: 10px;
          transition: transform 0.2s;
        }

        .dropdown:hover .dropdown-toggle::after {
          transform: rotate(180deg);
        }

        .dropdown-menu {
          position: absolute;
          top: 100%;
          left: 0;
          background: var(--white);
          border: 1px solid var(--gray-200);
          border-radius: 8px;
          padding: 8px 0;
          min-width: 180px;
          margin-top: 8px;
          opacity: 0;
          visibility: hidden;
          transform: translateY(-10px);
          transition: all 0.2s;
          box-shadow: 0 10px 40px rgba(0,0,0,0.1);
        }

        .dropdown:hover .dropdown-menu {
          opacity: 1;
          visibility: visible;
          transform: translateY(0);
        }

        .dropdown-menu a {
          display: block;
          padding: 10px 20px;
          color: var(--gray-900);
          text-decoration: none;
          font-size: 14px;
          transition: background 0.2s;
        }

        .dropdown-menu a:hover {
          background: var(--gray-100);
        }

        /* Nested Dropdown */
        .dropdown.nested {
          position: relative;
        }

        .dropdown.nested > a {
          display: flex;
          align-items: center;
          justify-content: space-between;
          width: 100%;
          cursor: pointer;
        }

        .dropdown.nested > a::after {
          content: '→';
          font-size: 12px;
          margin-left: auto;
          transition: transform 0.2s;
        }

        .dropdown.nested:hover > a::after {
          transform: translateX(2px);
        }

        /* Nested dropdown menu - explicitly hidden */
        .dropdown > .dropdown-menu > .dropdown.nested > .dropdown-menu {
          position: absolute;
          top: 0;
          left: 100%;
          margin-top: 0;
          margin-left: 8px;
          opacity: 0;
          visibility: hidden;
          transform: translateX(-10px);
          transition: all 0.2s;
          pointer-events: none;
        }

        /* Show ONLY when hovering the nested dropdown itself, not the parent */
        .dropdown > .dropdown-menu > .dropdown.nested:hover > .dropdown-menu {
          opacity: 1;
          visibility: visible;
          transform: translateX(0);
          pointer-events: auto;
        }

        .nav-actions {
          display: flex;
          align-items: center;
          gap: 12px;
        }

        .btn {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          padding: 10px 20px;
          border-radius: 8px;
          font-weight: 600;
          font-size: 14px;
          text-decoration: none;
          transition: all 0.2s;
          border: none;
          cursor: pointer;
        }

        .btn-primary { background: var(--blue-600); color: var(--white); }
        .btn-primary:hover { background: var(--blue-700); }
        .btn-outline { background: transparent; color: var(--gray-900); border: 1px solid var(--gray-200); }
        .btn-outline:hover { border-color: var(--blue-600); color: var(--blue-600); }
        .btn-gold { background: var(--gold); color: var(--gray-900); }
        .btn-gold:hover { background: var(--gold-dark); }
        .btn-secondary { background: var(--white); color: var(--gray-900); border: 1px solid var(--gray-200); }
        .btn-secondary:hover { background: var(--gray-100); }

        .mobile-toggle {
          display: none;
          flex-direction: column;
          gap: 5px;
          background: none;
          border: none;
          cursor: pointer;
          padding: 8px;
        }
        .mobile-toggle span { width: 22px; height: 2px; background: var(--gray-900); }

        .mobile-menu {
          display: none;
          position: fixed;
          top: 72px;
          left: 0;
          right: 0;
          background: var(--white);
          border-bottom: 1px solid var(--gray-200);
          padding: 16px 24px;
          max-height: calc(100vh - 72px);
          overflow-y: auto;
        }
        .mobile-menu.active { display: block; }
        .mobile-menu a {
          display: block;
          padding: 12px 0;
          color: var(--gray-900);
          text-decoration: none;
          font-weight: 500;
          border-bottom: 1px solid var(--gray-100);
        }
        .mobile-menu .mobile-section {
          font-size: 12px;
          color: var(--gray-500);
          text-transform: uppercase;
          margin-top: 16px;
          margin-bottom: 8px;
        }

        .modal-overlay {
          display: none;
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0,0,0,0.5);
          z-index: 2000;
          align-items: center;
          justify-content: center;
        }
        .modal-overlay.active { display: flex; }

        .modal {
          background: var(--white);
          border-radius: 16px;
          padding: 32px;
          width: 100%;
          max-width: 440px;
          margin: 24px;
          box-shadow: 0 20px 60px rgba(0,0,0,0.2);
        }

        .modal h3 {
          font-size: 24px;
          font-weight: 700;
          margin-bottom: 24px;
        }

        .modal-field {
          margin-bottom: 20px;
        }

        .modal-field label {
          display: block;
          font-size: 14px;
          font-weight: 500;
          color: var(--gray-500);
          margin-bottom: 8px;
        }

        .modal-field input,
        .modal-field select {
          width: 100%;
          padding: 14px 16px;
          border: 1px solid var(--gray-200);
          border-radius: 8px;
          font-size: 15px;
          font-family: inherit;
        }

        .modal-field input:focus,
        .modal-field select:focus {
          outline: none;
          border-color: var(--blue-600);
        }

        .modal-checkbox {
          display: flex;
          align-items: flex-start;
          gap: 12px;
          margin-bottom: 24px;
        }

        .modal-checkbox input {
          width: 18px;
          height: 18px;
          margin-top: 2px;
          accent-color: var(--blue-600);
        }

        .modal-checkbox span {
          font-size: 14px;
          color: var(--gray-500);
          line-height: 1.5;
        }

        .modal-buttons {
          display: flex;
          gap: 12px;
        }

        .modal-buttons .btn {
          flex: 1;
          justify-content: center;
          padding: 14px 20px;
        }

        .hero {
          padding: 140px 0 80px;
          background: linear-gradient(180deg, var(--off-white) 0%, var(--white) 100%);
          text-align: center;
        }

        .hero-label {
          display: inline-block;
          background: var(--white);
          border: 1px solid var(--gray-200);
          padding: 8px 20px;
          border-radius: 30px;
          font-size: 13px;
          font-weight: 600;
          color: var(--gray-500);
          margin-bottom: 24px;
        }

        .hero h1 {
          font-size: clamp(36px, 5vw, 52px);
          font-weight: 800;
          line-height: 1.1;
          margin-bottom: 24px;
          letter-spacing: -0.02em;
        }

        .hero h1 .gold { color: var(--gold); }

        .hero-quote {
          font-family: 'Crimson Text', serif;
          font-size: 22px;
          font-style: italic;
          color: var(--gray-500);
          max-width: 640px;
          margin: 0 auto;
          line-height: 1.5;
          letter-spacing: 0.3px;
        }

        .principles-section {
          padding: 56px 0;
        }

        .section-header {
          text-align: center;
          margin-bottom: 36px;
        }

        .section-label {
          display: inline-block;
          background: var(--blue-600);
          color: var(--white);
          padding: 6px 16px;
          border-radius: 20px;
          font-size: 12px;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          margin-bottom: 16px;
        }

        .section-title {
          font-size: clamp(28px, 4vw, 36px);
          font-weight: 800;
          margin-bottom: 12px;
        }

        .section-subtitle {
          font-size: 17px;
          color: var(--gray-500);
        }

        .principles-grid {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 24px;
        }

        .principle-card {
          background: var(--off-white);
          padding: 32px;
          border-radius: 16px;
          transition: all 0.2s;
        }

        .principle-card:hover {
          transform: translateY(-4px);
          box-shadow: 0 12px 32px rgba(0,0,0,0.08);
        }

        .principle-number {
          font-size: 48px;
          font-weight: 800;
          color: var(--gold);
          line-height: 1;
          margin-bottom: 16px;
        }

        .principle-title {
          font-size: 20px;
          font-weight: 700;
          margin-bottom: 12px;
        }

        .principle-text {
          font-size: 15px;
          color: var(--gray-500);
          line-height: 1.7;
        }

        .manifesto-cta {
          padding: 80px 0;
          text-align: center;
        }

        .manifesto-box {
          max-width: 640px;
          margin: 0 auto;
          padding: 48px;
          background: linear-gradient(135deg, var(--gray-900) 0%, #1e293b 100%);
          border-radius: 20px;
          color: var(--white);
        }

        .manifesto-box h2 {
          font-size: 28px;
          font-weight: 800;
          margin-bottom: 12px;
        }

        .manifesto-box p {
          font-family: 'Crimson Text', serif;
          font-size: 18px;
          font-style: italic;
          color: rgba(255,255,255,0.7);
          margin-bottom: 28px;
        }

        .cta-section {
          padding: 80px 0;
          background: linear-gradient(135deg, var(--blue-600) 0%, var(--blue-700) 100%);
          text-align: center;
        }

        .cta-section h2 {
          font-size: clamp(28px, 4vw, 36px);
          font-weight: 800;
          color: var(--white);
          margin-bottom: 12px;
        }

        .cta-section p {
          color: rgba(255,255,255,0.7);
          font-size: 17px;
          margin-bottom: 32px;
        }

        footer {
          background: var(--gray-900);
          color: var(--white);
          padding: 60px 0 40px;
        }

        .footer-content {
          display: grid;
          grid-template-columns: 1fr 2fr 1fr;
          gap: 40px;
          margin-bottom: 40px;
        }

        .footer-brand h3 {
          font-size: 24px;
          margin-bottom: 12px;
        }

        .footer-brand p {
          color: rgba(255,255,255,0.6);
          font-size: 14px;
          line-height: 1.6;
        }

        .footer-links h4 {
          font-size: 14px;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          margin-bottom: 20px;
          color: rgba(255,255,255,0.5);
        }

        .footer-grid {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 20px;
        }

        .footer-grid a {
          color: rgba(255,255,255,0.8);
          text-decoration: none;
          font-size: 14px;
          transition: color 0.2s;
        }

        .footer-grid a:hover {
          color: var(--white);
        }

        .footer-social h4 {
          font-size: 14px;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          margin-bottom: 20px;
          color: rgba(255,255,255,0.5);
        }

        .social-links {
          display: flex;
          gap: 12px;
        }

        .social-links a {
          width: 40px;
          height: 40px;
          background: rgba(255,255,255,0.1);
          border-radius: 8px;
          display: flex;
          align-items: center;
          justify-content: center;
          color: rgba(255,255,255,0.6);
          transition: all 0.2s;
        }

        .social-links a:hover {
          background: rgba(255,255,255,0.2);
          color: var(--white);
        }

        .footer-bottom {
          padding-top: 40px;
          border-top: 1px solid rgba(255,255,255,0.1);
          text-align: center;
          color: rgba(255,255,255,0.5);
          font-size: 14px;
        }

        @media (max-width: 768px) {
          .nav-menu, .nav-actions { display: none; }
          .mobile-toggle { display: flex; }

          .hero { padding: 100px 0 48px; }
          .hero h1 { font-size: 32px; }
          .hero-quote { font-size: 18px; }

          .principles-grid { grid-template-columns: 1fr; }

          .manifesto-box { padding: 32px 24px; }

          .footer-content { grid-template-columns: 1fr; }
          .footer-grid { grid-template-columns: repeat(2, 1fr); }

          .modal-buttons { flex-direction: column; }
        }
      `}</style>

        {/* Modal */}
     <div 
        className={`modal-overlay ${modalOpen ? 'active' : ''}`}
        onClick={() => setModalOpen(false)}
      >
        <div className="modal" onClick={(e) => e.stopPropagation()}>
          <h3>Join the Waitlist</h3>
          
          {submitMessage && (
            <div className={`submit-message ${submitMessage.includes('Thank you') ? 'success' : 'error'}`}>
              {submitMessage}
            </div>
          )}

          <form onSubmit={handleSubmit}>
            <div className="modal-field">
              <input 
                type="email" 
                name="email"
                placeholder="Enter your email" 
                value={formData.email}
                onChange={handleInputChange}
                required 
                disabled={isSubmitting}
              />
            </div>
            <div className="modal-field">
              <label>I am a...</label>
              <select 
                name="type"
                value={formData.type}
                onChange={handleInputChange}
                required
                disabled={isSubmitting}
              >
                <option value="user">User</option>
                <option value="merchant">Merchant</option>
                <option value="enterprise">Enterprise</option>
              </select>
            </div>
            <div className="modal-checkbox">
              <input 
                type="checkbox" 
                name="ageConfirm"
                id="ageConfirm" 
                checked={formData.ageConfirm}
                onChange={handleInputChange}
                required 
                disabled={isSubmitting}
              />
              <span>I am 18 or older and agree to the <a target='_blank' rel='noopener noreferrer' href="/terms">Terms of Service and Privacy Policy</a></span>
            </div>
            <div className="modal-buttons">
              <button 
                type="submit" 
                className="btn btn-primary"
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Submitting...' : 'Join Waitlist'}
              </button>
              <button 
                type="button"
                onClick={() => setModalOpen(false)} 
                className="btn btn-outline"
                disabled={isSubmitting}
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      </div>

       <Header onJoinWaitlist={() => setModalOpen(true)} />

      {/* Hero */}
      <section className="hero">
        <div className="container">
          <div className="hero-label">Our Vision</div>
          <h1>Trust as <span className="gold">Currency</span></h1>
          <p className="hero-quote">
            Loyalty should be remembered, honored, and circulated. Not gamified, not manipulated — never expired.
          </p>
        </div>
      </section>

      {/* The Fracture */}
      <section style={{padding: '72px 0', background: 'var(--off-white, #F8F7F4)'}}>
        <div className="container-narrow" style={{maxWidth: '720px', margin: '0 auto', padding: '0 24px'}}>
          <p style={{fontSize: '18px', lineHeight: 1.7, color: 'var(--gray-900)', marginBottom: '20px'}}>
            $300 billion in loyalty value is issued every year. Nearly half is never redeemed — not because customers forgot, but because the systems never honored the promise. Points are offered generously and devalued silently. The value is determined at redemption, not earning. Customers become data. Loyalty becomes extraction.
          </p>
          <p style={{fontSize: '18px', lineHeight: 1.7, color: 'var(--gray-500)', marginBottom: '20px'}}>
            The industry has optimized for retention, not trust. It measures customer lifetime value but never asks: did we guarantee the value we promised them?
          </p>
          <p style={{fontSize: '18px', lineHeight: 1.7, color: 'var(--gray-900)', fontWeight: 600}}>
            Rewardly Protocol inverts this entirely. Value is guaranteed at the moment you earn it — locked by oracle, permanent by design.
          </p>
        </div>
      </section>

      {/* The Opportunity */}
      <section style={{padding: '72px 0', background: '#fff'}}>
        <div className="container">
          <div style={{textAlign: 'center', marginBottom: '48px'}}>
            <div className="section-label" style={{display: 'inline-block', background: 'var(--blue-600)', color: '#fff', padding: '6px 16px', borderRadius: '20px', fontSize: '12px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '16px'}}>The Opportunity</div>
            <h2 className="section-title">The White Space Is Enormous</h2>
          </div>
          <div style={{display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '24px', marginBottom: '32px'}}>
            <div className="stat-card" style={{textAlign: 'center', padding: '32px 24px'}}>
              <div style={{fontSize: '40px', fontWeight: 800, color: 'var(--blue-600)', marginBottom: '8px'}}>$300B</div>
              <div style={{fontSize: '16px', fontWeight: 600, color: 'var(--gray-900)', marginBottom: '4px'}}>Issued Annually</div>
              <div style={{fontSize: '13px', color: 'var(--gray-500)'}}>Global loyalty value created each year</div>
            </div>
            <div className="stat-card" style={{textAlign: 'center', padding: '32px 24px'}}>
              <div style={{fontSize: '40px', fontWeight: 800, color: 'var(--blue-600)', marginBottom: '8px'}}>Near Zero</div>
              <div style={{fontSize: '16px', fontWeight: 600, color: 'var(--gray-900)', marginBottom: '4px'}}>Universal Loyalty Platforms</div>
              <div style={{fontSize: '13px', color: 'var(--gray-500)'}}>No protocol-grade infrastructure serving all 10 industries</div>
            </div>
            <div className="stat-card" style={{textAlign: 'center', padding: '32px 24px'}}>
              <div style={{fontSize: '40px', fontWeight: 800, color: 'var(--blue-600)', marginBottom: '8px'}}>116K+</div>
              <div style={{fontSize: '16px', fontWeight: 600, color: 'var(--gray-900)', marginBottom: '4px'}}>Multi-Merchant Venues</div>
              <div style={{fontSize: '13px', color: 'var(--gray-500)'}}>Shopping centers, campuses, airports — no loyalty infrastructure</div>
            </div>
          </div>
          <p style={{textAlign: 'center', color: 'var(--gray-500)', fontSize: '15px', maxWidth: '640px', margin: '0 auto'}}>
            50+ apps track your points. 3-5 legacy players do enterprise integration. Zero provide earn-time value commitment across industries. Rewardly Protocol is the only infrastructure that guarantees reward value at the moment of earning — across retail, healthcare, government, non-profit, and 6 more verticals. 150+ promotion templates. Patent pending.
          </p>
        </div>
      </section>

      {/* The Path */}
      <section style={{padding: '72px 0', background: 'var(--off-white, #F8F7F4)'}}>
        <div className="container">
          <div style={{textAlign: 'center', marginBottom: '48px'}}>
            <div className="section-label" style={{display: 'inline-block', background: 'var(--blue-600)', color: '#fff', padding: '6px 16px', borderRadius: '20px', fontSize: '12px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '16px'}}>The Path</div>
            <h2 className="section-title">Where We Are Going</h2>
          </div>
          <div style={{display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '24px'}}>
            <div style={{background: '#fff', border: '1px solid var(--gray-200)', borderTop: '4px solid var(--blue-600)', borderRadius: '12px', padding: '28px'}}>
              <div style={{fontSize: '11px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '1px', color: 'var(--gold-dark, #B8922A)', marginBottom: '8px'}}>Year 1</div>
              <h3 style={{fontSize: '20px', fontWeight: 700, marginBottom: '16px', color: 'var(--gray-900)'}}>Coalition Launch</h3>
              <div style={{display: 'inline-block', padding: '3px 10px', borderRadius: '4px', background: 'rgba(5,150,105,0.1)', color: 'var(--green-600)', fontSize: '11px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '12px'}}>We are here</div>
              <ul style={{listStyle: 'none', padding: 0, margin: 0}}>
                <li style={{padding: '6px 0', fontSize: '14px', color: 'var(--gray-600)', borderBottom: '1px solid var(--gray-100)'}}>38+ smart contracts deployed on Amoy testnet</li>
                <li style={{padding: '6px 0', fontSize: '14px', color: 'var(--gray-600)', borderBottom: '1px solid var(--gray-100)'}}>202 Foundry tests passing, internal audit complete</li>
                <li style={{padding: '6px 0', fontSize: '14px', color: 'var(--gray-600)', borderBottom: '1px solid var(--gray-100)'}}>Patent filed: PPA (US) + PCT International</li>
                <li style={{padding: '6px 0', fontSize: '14px', color: 'var(--gray-600)'}}>Target: first coalitions live, 100K active users</li>
              </ul>
            </div>
            <div style={{background: '#fff', border: '1px solid var(--gray-200)', borderTop: '4px solid var(--blue-600)', borderRadius: '12px', padding: '28px'}}>
              <div style={{fontSize: '11px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '1px', color: 'var(--gold-dark, #B8922A)', marginBottom: '8px'}}>Year 2</div>
              <h3 style={{fontSize: '20px', fontWeight: 700, marginBottom: '16px', color: 'var(--gray-900)'}}>Cross-Coalition Network</h3>
              <ul style={{listStyle: 'none', padding: 0, margin: 0}}>
                <li style={{padding: '6px 0', fontSize: '14px', color: 'var(--gray-600)', borderBottom: '1px solid var(--gray-100)'}}>Earn at one ecosystem, redeem at another</li>
                <li style={{padding: '6px 0', fontSize: '14px', color: 'var(--gray-600)', borderBottom: '1px solid var(--gray-100)'}}>Network effects create switching costs</li>
                <li style={{padding: '6px 0', fontSize: '14px', color: 'var(--gray-600)'}}>Target: 500K users, 50 coalitions</li>
              </ul>
            </div>
            <div style={{background: '#fff', border: '1px solid var(--gray-200)', borderTop: '4px solid var(--blue-600)', borderRadius: '12px', padding: '28px'}}>
              <div style={{fontSize: '11px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '1px', color: 'var(--gold-dark, #B8922A)', marginBottom: '8px'}}>Year 3+</div>
              <h3 style={{fontSize: '20px', fontWeight: 700, marginBottom: '16px', color: 'var(--gray-900)'}}>Enterprise Onboarding</h3>
              <ul style={{listStyle: 'none', padding: 0, margin: 0}}>
                <li style={{padding: '6px 0', fontSize: '14px', color: 'var(--gray-600)', borderBottom: '1px solid var(--gray-100)'}}>Airlines and hotels join to access the user base</li>
                <li style={{padding: '6px 0', fontSize: '14px', color: 'var(--gray-600)', borderBottom: '1px solid var(--gray-100)'}}>Bidirectional: airline miles ↔ RWLY ↔ loyalty points</li>
                <li style={{padding: '6px 0', fontSize: '14px', color: 'var(--gray-600)'}}>Target: 10M users, protocol standard</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* What Success Looks Like */}
      <section style={{padding: '72px 0', background: '#fff'}}>
        <div className="container">
          <div style={{textAlign: 'center', marginBottom: '48px'}}>
            <h2 className="section-title">What Success Looks Like</h2>
          </div>
          <div style={{display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '20px', maxWidth: '900px', margin: '0 auto'}}>
            {[
              'A hospital rewards a patient for completing preventive care — points they spend at their local pharmacy',
              'A government transit rider earns rewards that never expire, redeemable anywhere in the network',
              'A temple recognizes a volunteer\'s 100 hours of service with tokens their family can use',
              'Any organization, in any industry, launches a loyalty program in weeks — not years'
            ].map((statement, i) => (
              <div key={i} style={{background: 'var(--off-white, #F8F7F4)', border: '1px solid var(--gray-200)', borderLeft: '4px solid var(--gold, #D4A853)', borderRadius: '8px', padding: '20px 24px'}}>
                <p style={{fontSize: '15px', lineHeight: 1.6, color: 'var(--gray-900)', margin: 0}}>{statement}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Cross-link to Manifesto */}
      <section style={{padding: '32px 0', background: 'var(--off-white, #F8F7F4)', textAlign: 'center'}}>
        <div className="container">
          <a href="/manifesto" style={{fontSize: '16px', fontWeight: 600, color: 'var(--blue-600)', textDecoration: 'none'}}>Read the philosophy behind our vision →</a>
        </div>
      </section>

      {/* Manifesto CTA */}
      <section className="manifesto-cta">
        <div className="container">
          <div className="manifesto-box">
            <h2>Read the Manifesto</h2>
            <p>The complete philosophy behind Rewardly Protocol's approach to loyalty.</p>
            <a href="/manifesto" className="btn btn-gold">Read the Manifesto →</a>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="cta-section">
        <div className="container">
          <h2>Build the Future With Us</h2>
          <p>Be among the first to experience loyalty without limits.</p>
          <div style={{display: 'flex', gap: '16px', justifyContent: 'center', flexWrap: 'wrap'}}>
            <button onClick={() => setModalOpen(true)} className="btn btn-gold" style={{padding: '16px 32px', fontSize: '16px'}}>Join Waitlist →</button>
            <a href="/manifesto" style={{display: 'inline-flex', alignItems: 'center', padding: '16px 32px', fontSize: '16px', borderRadius: '8px', fontWeight: 600, textDecoration: 'none', border: '1px solid rgba(255,255,255,0.3)', color: '#FFFFFF', transition: 'all 0.2s'}}>Read the Manifesto →</a>
          </div>
        </div>
      </section>

      {/* Footer */}
      <Footer />
    </div>
  );
};

export default VisionPage;